/**
 * Russian for the 09 client list and the new-client page.
 *
 * `uz-latin` is the only locale shipped (D12); this dictionary is kept complete for the keys
 * these screens own so a second locale is a lookup away. The type makes a missing key a
 * compile error, so the two dictionaries cannot drift.
 */
import type { TranslationKey } from "./uz";

export type Clients09Key = Extract<TranslationKey, `clientList.${string}` | `clientNew.${string}`>;

export const ru09Clients: Record<Clients09Key, string> = {
  "clientList.subtitle": "{branch} · {count} клиентов",
  "clientList.allBranches": "Все филиалы",
  "clientList.export": "Выгрузить в Excel",
  "clientList.exportHint": "Каждая выгрузка записывается в журнал",
  "clientList.exportFailed": "Не удалось выгрузить файл",
  "clientList.new": "+ Новый клиент",
  "clientList.search": "Имя, телефон или паспорт",
  "clientList.today": "Возможности на сегодня",
  "clientList.today.returned": "Вернулись из поездки",
  "clientList.today.returnedHint": "спросить отзыв",
  "clientList.today.birthday": "День рождения",
  "clientList.today.birthdayHint": "на этой неделе",
  "clientList.today.passport": "Истекает паспорт",
  "clientList.today.passportHint": "в течение 6 месяцев",
  "clientList.today.yearAgo": "Путешествовали год назад",
  "clientList.today.yearAgoHint": "повторное предложение",
  "clientList.today.clear": "Снять фильтр возможности",
  "clientList.filter.branch": "Филиал",
  "clientList.filter.owner": "Ответственный",
  "clientList.filter.source": "Источник",
  "clientList.filter.destination": "Направление",
  "clientList.filter.any": "Все",
  "clientList.filter.reset": "Сбросить фильтры",
  "clientList.col.select": "Выбор",
  "clientList.col.client": "Клиент",
  "clientList.col.group": "Группа",
  "clientList.col.trips": "Поездки",
  "clientList.col.total": "Общая сумма",
  "clientList.col.lastTrip": "Последняя поездка",
  "clientList.col.next": "Следующая возможность",
  "clientList.col.source": "Источник",
  "clientList.col.owner": "Ответственный",
  "clientList.selectAll": "Выбрать всех клиентов на странице",
  "clientList.selectRow": "Выбрать: {name}",
  "clientList.referredBy": "Рекомендация: {name}",
  "clientList.source.instagramComment": "Комментарий в Instagram",
  "clientList.source.website": "Сайт",
  "clientList.source.import": "Импорт",
  "clientList.bulk.selected": "Выбрано: {count}",
  "clientList.bulk.clear": "Снять выбор",
  "clientList.bulk.telegram": "Отправить сообщение в Telegram",
  "clientList.bulk.owner": "Сменить ответственного",
  "clientList.bulk.tag": "Добавить тег",
  "clientList.bulk.consentSplit": "Из {total} будет отправлено {eligible} — {skipped} не дали согласия",
  "clientList.bulk.consentAll": "Будет отправлено {eligible} — все дали согласие",
  "clientList.bulk.noneEligible": "Никто из выбранных не дал согласия на рассылку",
  "clientList.bulk.text": "Текст сообщения",
  "clientList.bulk.send": "Отправить",
  "clientList.bulk.queued": "Сообщение поставлено в очередь",
  "clientList.bulk.progress": "Отправлено: {sent} / {eligible}",
  "clientList.bulk.progressFailed": "Ошибок: {failed}",
  "clientList.bulk.done": "Отправка завершена",
  "clientList.bulk.newOwner": "Новый ответственный",
  "clientList.bulk.noOwner": "Без ответственного",
  "clientList.bulk.ownerSaved": "Ответственный изменён у {count} клиентов",
  "clientList.bulk.tagName": "Тег",
  "clientList.bulk.tagSaved": "Тег добавлен {count} клиентам",
  "clientList.bulk.failed": "Не удалось выполнить",
  "clientList.bulk.cancel": "Отмена",
  "clientList.bulk.save": "Сохранить",
  "clientNew.breadcrumb": "Клиенты",
  "clientNew.title": "Новый клиент",
  "clientNew.quick.title": "Быстрый ввод",
  "clientNew.quick.placeholder":
    "Например: Малика Юсупова, +998 90 111 22 33, Дубай, 10–17 декабря, 2 взрослых и ребёнок 7 лет, бюджет 3000 долларов",
  "clientNew.quick.label": "Что сказал клиент",
  "clientNew.quick.fill": "Заполнить поля",
  "clientNew.quick.filled": "Заполнено полей: {count} — проверьте",
  "clientNew.quick.nothing": "В тексте не найдено полей",
  "clientNew.quick.failed": "Не удалось заполнить поля",
  "clientNew.ai": "AI",
  "clientNew.aiHint": "Заполнено AI — проверьте",
  "clientNew.source.title": "Откуда пришёл клиент?",
  "clientNew.source.required": "Выберите источник",
  "clientNew.source.walkIn": "Пришёл в офис",
  "clientNew.source.phone": "Телефонный звонок",
  "clientNew.source.instagramAd": "Реклама в Instagram",
  "clientNew.source.instagram": "Instagram",
  "clientNew.source.telegram": "Telegram",
  "clientNew.source.referral": "Рекомендация",
  "clientNew.source.corporate": "Корпоративный",
  "clientNew.referrer": "Кто порекомендовал",
  "clientNew.referrerSearch": "Поиск клиента",
  "clientNew.referrerPlaceholder": "Выберите клиента из базы",
  "clientNew.referrerEmpty": "Клиент не найден",
  "clientNew.contact.title": "Контактные данные",
  "clientNew.contact.name": "Имя, фамилия",
  "clientNew.contact.phone": "Телефон",
  "clientNew.contact.phoneNew": "Новый номер — в базе нет совпадений",
  "clientNew.contact.phoneExists": "Этот номер уже есть в базе: {name}",
  "clientNew.contact.phoneHidden": "Этот номер есть в базе у клиента другого сотрудника",
  "clientNew.contact.openExisting": "Открыть существующего клиента",
  "clientNew.contact.handle": "Telegram или Instagram",
  "clientNew.contact.handleKind": "Сеть",
  "clientNew.contact.email": "Email",
  "clientNew.contact.birthDate": "Дата рождения",
  "clientNew.contact.language": "Язык общения",
  "clientNew.contact.languageAny": "Не выбран",
  "clientNew.language.uzLatn": "Узбекский (латиница)",
  "clientNew.language.uzCyrl": "Узбекский (кириллица)",
  "clientNew.language.ru": "Русский",
  "clientNew.language.en": "Английский",
  "clientNew.need.title": "Что нужно?",
  "clientNew.service.tour": "Тур",
  "clientNew.service.visa": "Виза",
  "clientNew.service.avia": "Авиабилет",
  "clientNew.service.business": "Деловая поездка",
  "clientNew.need.destination": "Направление",
  "clientNew.need.destinationSearch": "Поиск направления",
  "clientNew.need.destinationPlaceholder": "Выберите из каталога",
  "clientNew.need.destinationEmpty": "Направление не найдено",
  "clientNew.need.destinationText": "Или запишите, как сказал клиент",
  "clientNew.need.departure": "Город вылета",
  "clientNew.need.departureAny": "Не выбран",
  "clientNew.need.dateFrom": "Дата вылета",
  "clientNew.need.dateTo": "Дата возвращения",
  "clientNew.need.flexible": "Гибкие даты (±3 дня)",
  "clientNew.need.adults": "Взрослые",
  "clientNew.need.children": "Дети",
  "clientNew.need.childAge": "Возраст {n}-го ребёнка",
  "clientNew.need.budget": "Бюджет",
  "clientNew.need.currency": "Валюта",
  "clientNew.need.hotel": "Требование к отелю",
  "clientNew.need.hotelAny": "Без разницы",
  "clientNew.need.hotelStars": "{n} звезды",
  "clientNew.consent": "Согласен получать акции и новости (через Telegram)",
  "clientNew.cancel": "Отмена",
  "clientNew.saveClient": "Сохранить только клиента",
  "clientNew.saveBoth": "Создать клиента и сделку",
  "clientNew.savedClient": "Клиент сохранён",
  "clientNew.savedBoth": "Клиент и сделка созданы",
  "clientNew.saveFailed": "Не сохранено. Проверьте поля и попробуйте снова.",
  "clientNew.duplicate": "Этот номер уже есть в базе",
  "clientNew.guide.title": "Сценарий разговора",
  "clientNew.guide.progress": "{answered} / {total}",
  "clientNew.guide.failed": "Не удалось загрузить сценарий",
  "clientNew.guide.done": "Ответ получен",
  "clientNew.tours.title": "Подходящие туры",
  "clientNew.tours.needDestination": "Выберите направление из каталога — подходящие туры появятся здесь",
  "clientNew.tours.found": "Найдено вариантов: {count}, самый дешёвый {price}",
  "clientNew.tours.foundNoPrice": "Найдено вариантов: {count}",
  "clientNew.tours.none": "Среди сохранённых предложений подходящих нет",
  "clientNew.tours.failed": "Не удалось загрузить подходящие туры",
  "clientNew.tours.open": "Посмотреть варианты",
};
